import {
  SegmentRoot,
  SegmentImageCard,
  SegmentImage,
  SegmentInputSection,
  SegmentAlarSection,
  SegmentAlarDescription,
  SegmentAlarRow,
} from "~/features/configurator/Shaft/substeps/ShaftMachiningSubstep/components/SegmentCommonStyles";
import { Card } from "~/features/configurator/Linear/components/Card";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { SimpleCsticNumberField } from "~/features/configurator/Shaft/components/SimpleCsticNumberField";
import { type CsticId, SimpleCsticId } from "~/features/configurator/Linear/models";
import { INCH_UNIT, METRIC_UNIT } from "~/features/configurator/Shaft/common/commonShaftConstants";
import { useMinMaxCsticLabel } from "~/features/configurator/Shaft/components/useMinMaxCsticLabel";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { SimpleCsticTextField } from "~/features/configurator/Shaft/components/SimpleCsticTextField";
import { radialThread } from "~/features/configurator/Shaft/inch/inchCsticConstants";

interface Props {
  className?: string;
}

const AUTOMATIC_ALAR = "S";
const MANUAL_ALAR = "A";

const CONFIG = {
  pdt: {
    label: "configurator.shaft.step.2.substep.3.extraFields.radialThread.title",
    csticId: SimpleCsticId(radialThread.pdt),
  },
  threadSpacingInch: {
    label: "configurator.shaft.step.2.substep.3.extraFields.radialThreadSpacing.title.inch",
    csticId: SimpleCsticId(radialThread.threadSpacing.inch),
  },
  threadSpacingMm: {
    label: "configurator.shaft.step.2.substep.3.extraFields.radialThreadSpacing.title.metric",
    csticId: SimpleCsticId(radialThread.threadSpacing.metric),
  },
  al: {
    inch: {
      label: "configurator.shaft.step.2.substep.3.extraFields.al.title.inch",
      value: SimpleCsticId(radialThread.aL.inch.value),
      min: SimpleCsticId(radialThread.aL.inch.min),
      max: SimpleCsticId(radialThread.aL.inch.max),
      unit: INCH_UNIT,
      csticsToSet: [
        { key: radialThread.aL.metric.value, value: null },
        (val: string | null) => ({ key: radialThread.aLaREnabler, value: val === null ? AUTOMATIC_ALAR : MANUAL_ALAR }),
      ],
    },
    mm: {
      label: "configurator.shaft.step.2.substep.3.extraFields.al.title.metric",
      unit: METRIC_UNIT,
      value: SimpleCsticId(radialThread.aL.metric.value),
      min: SimpleCsticId(radialThread.aL.metric.min),
      max: SimpleCsticId(radialThread.aL.metric.max),
      disabled: true,
    },
  },
  ar: {
    inch: {
      label: "configurator.shaft.step.2.substep.3.extraFields.ar.title.inch",
      value: SimpleCsticId(radialThread.aR.inch.value),
      min: SimpleCsticId(radialThread.aR.inch.min),
      max: SimpleCsticId(radialThread.aR.inch.max),
      unit: INCH_UNIT,
      disabled: true,
    },
    mm: {
      label: "configurator.shaft.step.2.substep.3.extraFields.ar.title.metric",
      value: SimpleCsticId(radialThread.aR.metric.value),
      min: SimpleCsticId(radialThread.aR.metric.min),
      max: SimpleCsticId(radialThread.aR.metric.max),
      unit: METRIC_UNIT,
      disabled: true,
    },
  },
  threadHoleAmount: {
    label: "configurator.shaft.step.2.substep.3.extraFields.holeQuantity.title",
    csticId: SimpleCsticId(radialThread.threadHoleAmount),
  },
};

interface AlarInputProps {
  label: string;
  value: CsticId;
  min: CsticId;
  max: CsticId;
  unit: string;
  csticsToNull?: string[];
  csticsToSet?: Array<CsticToSet | Resolver>;
  disabled?: boolean;
  decimalScale?: number;
  substepType?: SubstepType;
  required?: boolean;
  error?: boolean;
}

type CsticToSet =
  | {
      key: string;
      value: string | null;
    }
  | undefined;

type Resolver = (value: string | null) => CsticToSet;

export const AlarInput = ({
  min,
  max,
  value,
  unit,
  decimalScale = 2,
  substepType = SubstepType.ShaftMachining,
  ...rest
}: AlarInputProps) => {
  const helperLabel = useMinMaxCsticLabel({ minCsticId: min, maxCsticId: max, unit });
  return (
    <SimpleCsticNumberField
      csticId={value}
      substepType={substepType}
      decimalScale={decimalScale}
      helperLabel={helperLabel}
      {...rest}
    />
  );
};

export const InchShaftRadialThreadSegment = (props: Props) => {
  const t = useXmlConfiguratorTranslations().t!;

  function translate<T extends { label: string }>(data: T) {
    return { ...data, label: t(data.label) };
  }
  return (
    <SegmentRoot className={props.className}>
      <SegmentImageCard>
        <SegmentImage src={t("configurator.shaft.step.2.substep.3.extraFields.center.image")} alt="" />
      </SegmentImageCard>
      <SegmentInputSection>
        <SimpleCsticTextField {...translate(CONFIG.pdt)} substepType={SubstepType.ShaftMachining} disabled={true} />
        <SimpleCsticNumberField
          {...translate(CONFIG.threadSpacingInch)}
          substepType={SubstepType.ShaftMachining}
          disabled={true}
          decimalScale={2}
        />
        <SimpleCsticNumberField
          {...translate(CONFIG.threadSpacingMm)}
          substepType={SubstepType.ShaftMachining}
          disabled={true}
          decimalScale={2}
        />
        <SegmentAlarSection>
          <SegmentAlarDescription>{t("configurator.shaft.step.2.substep.3.extraFields.alar.title")}</SegmentAlarDescription>
          <SegmentAlarRow>
            <AlarInput {...translate(CONFIG.al.inch)} decimalScale={4} />
            <AlarInput {...translate(CONFIG.ar.inch)} decimalScale={4} />
          </SegmentAlarRow>
          <SegmentAlarRow>
            <AlarInput {...translate(CONFIG.al.mm)} />
            <AlarInput {...translate(CONFIG.ar.mm)} />
          </SegmentAlarRow>
        </SegmentAlarSection>
        <SimpleCsticNumberField
          {...translate(CONFIG.threadHoleAmount)}
          substepType={SubstepType.ShaftMachining}
          disabled={true}
          decimalScale={0}
        />
      </SegmentInputSection>
    </SegmentRoot>
  );
};
